/**
 * CharacterSheetEditor — a ficha do personagem num `Drawer` que entra pela
 * direita, com o X e o gesto de arrastar.
 *
 * Desde 2026-07-31 (fase 1 de docs/PLANO_MEMBROS_E_FICHA.md) este arquivo é só
 * o INVÓLUCRO; o conteúdo mora em `FichaDoPersonagem`. A API pública não mudou.
 *
 * Quando usar este invólucro, e não o modo empilhado do `SidePanel`:
 * **sobreposição é para interrupção, aba é para navegação.** O `Drawer` é a
 * revelação da cerimônia de sorteio (a mesa te chama, tem de aparecer por cima
 * do que você estiver fazendo) e o desktop, onde há espaço para a ficha e o
 * chat ao mesmo tempo.
 */

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
} from '@/components/ui/drawer'
import { X } from 'lucide-react'
import { FichaDoPersonagem, type FichaDoPersonagemProps } from './FichaDoPersonagem'

type CharacterSheetEditorProps = FichaDoPersonagemProps

export function CharacterSheetEditor({
  open,
  onOpenChange,
  ...daFicha
}: CharacterSheetEditorProps) {
  const { character } = daFicha

  // Sem personagem, o Drawer inteiro sai na hora — não só o conteúdo.
  //
  // Esta guarda existia antes da extração e precisou ser reposta AQUI: deixando
  // só a de dentro do `FichaDoPersonagem`, o invólucro sobrevivia e o vaul
  // animava a saída de um painel vazio. Acontece de verdade quando alguém
  // apaga um personagem enquanto outra pessoa está com a ficha dele aberta.
  if (!character) return null

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        data-testid="ficha-personagem"
        /* Altura em `dvh`, não em `vh` (2026-07-31).
         *
         * O conteúdo ancora em `fixed inset-y-0`, que é o *layout viewport* —
         * no Chrome/Safari do celular, a tela COM A BARRA ESCONDIDA. Com a
         * barra à mostra, o fim da ficha cai atrás dela, e o que mora lá é
         * justamente o que não rola junto: "Pedir teste de atributo" e "Salvar
         * Alterações". Rolar a página escondia a barra e a ficha "encaixava" —
         * daí o relato do Paulo de que às vezes cabia e às vezes não, no mesmo
         * aparelho e na mesma ficha.
         *
         * `dvh` acompanha a barra, então o rodapé fica sempre alcançável.
         *
         * O `bottom-auto` desfaz o `inset-y-0` da base: com top, bottom e
         * height definidos ao mesmo tempo o CSS descarta um deles, e é melhor
         * dizer qual do que depender da regra de desempate. Ele vai na forma
         * `data-[vaul-drawer-direction=right]:` de propósito — a base aplica o
         * `inset-y-0` por essa mesma variante, e um `bottom-auto` simples
         * perderia por especificidade sem dar nenhum sinal. */
        className="w-full sm:max-w-md bg-game-secondary dark:bg-gradient-to-b dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950 border-l border-game-border-primary dark:border-zinc-800 p-0 flex flex-col data-[vaul-drawer-direction=right]:bottom-auto h-[100dvh] max-h-[100dvh] overflow-hidden"
      >
        {/* O vaul exige um título acessível dentro do Content. Ele é oculto
            porque o nome do personagem já está desenhado no cabeçalho da ficha
            — e a ficha não pode usar `DrawerTitle`, que quebraria no modo
            empilhado, onde não existe Drawer nenhum. */}
        <DrawerTitle className="sr-only">
          {character ? `Ficha de ${character.name}` : 'Ficha do personagem'}
        </DrawerTitle>

        {/* Fechar. O gesto de arrastar é o caminho principal no celular, mas
            o botão fica: no desktop não se arrasta, e quem não descobriu o
            gesto precisa de uma saída visível. 44px de área de toque.

            Mora no INVÓLUCRO, não na ficha: cada modo tem a sua saída (aqui o
            X, no empilhado o "‹ Membros"), e é isso que evita um `if` de
            apresentação dentro do conteúdo. */}
        <DrawerClose
          aria-label="Fechar"
          className="absolute top-2 right-2 z-10 p-3 rounded-full text-game-muted dark:text-zinc-400 hover:bg-game-hover dark:hover:bg-white/10 transition-colors"
        >
          <X className="h-5 w-5" />
        </DrawerClose>

        <FichaDoPersonagem open={open} onOpenChange={onOpenChange} {...daFicha} />
      </DrawerContent>
    </Drawer>
  )
}
